<template>
  <div
    v-if="!conversationSize && isFetchingList"
    class="flex items-center h-full justify-center bg-white"
  >
    <spinner size="" />
  </div>
  <div v-else class="home" :class="{'chatbot-flow-active': hasAConnectedAgentBot}">
    <div
      class="header-wrap bg-white"
      :class="{ expanded: !isHeaderCollapsed, collapsed: isHeaderCollapsed }"
    >
      <transition
        enter-active-class="transition-all delay-200 duration-300 ease"
        leave-active-class="transition-all duration-200 ease-in"
        enter-class="opacity-0 transform"
        enter-to-class="opacity-100 transform"
        leave-class="opacity-100 transform"
        leave-to-class="opacity-0 transform"
      >
        <chat-header-expanded
          v-if="currentView !== 'messageView' || isSurfaceKbEnabled"
          :intro-heading="channelConfig.welcomeTitle"
          :intro-body="channelConfig.welcomeTagline"
          :avatar-url="channelConfig.avatarUrl"
          :show-popout-button="showPopoutButton"
          :conversation-attributes="conversationAttributes"
          :chat-tab-props="chatTabProps"
        />
        <chat-header
          v-if="currentView === 'messageView' && !isSurfaceKbEnabled"
          :title="channelConfig.websiteName"
          :avatar-url="channelConfig.avatarUrl"
          :show-popout-button="showPopoutButton"
          :available-agents="availableAgents"
          :conversation-attributes="conversationAttributes"
          :chat-tab-props="chatTabProps"
        />
      </transition>
    </div>
    <banner />
    <div class="flex flex-1 overflow-auto">
      <conversation-wrap
        v-if="currentView === 'messageView'"
        :grouped-messages="groupedMessages"
      />
      <pre-chat-form
        v-if="currentView === 'preChatFormView'"
        :options="preChatFormOptions"
        :dropDownImageUrl="dropDownSymbolImageUrl"
        :dropDownImageAlt="dropDownSymbolImageAlt"
        @handleChatBot="handleChatbotFlowOnNewConversation"
      />
      <contact-form v-if="currentView === 'contactFormView' && !isLoadingAvailableAgents" @cfSuccessScreenExpired="onCfSuccessScreenExpired"/>
    </div>
    <div class="footer-wrap">
      <transition
        enter-active-class="transition-all delay-300 duration-300 ease"
        leave-active-class="transition-all duration-200 ease-in"
        enter-class="opacity-0 transform"
        enter-to-class="opacity-100 transform translate-y-0"
        leave-class="opacity-100 transform translate-y-0"
        leave-to-class="opacity-0 transform "
      >
        <div v-if="currentView === 'messageView'" class="input-wrap">
          <hiver-branding />
          <chat-footer :available-agents="availableAgents" :messages="groupedMessages" @startNewConversation="onStartNewConversation"/>
        </div>
        <team-availability
          v-if="currentView === 'cardView'"
          :available-agents="availableAgents"
          @start-conversation="startConversation"
        />
      </transition>
    </div>
  </div>
</template>

<script>
import ChatFooter from 'widget/components/ChatFooter.vue';
import ChatHeaderExpanded from 'widget/components/ChatHeaderExpanded.vue';
import ChatHeader from 'widget/components/ChatHeader.vue';
import ConversationWrap from 'widget/components/ConversationWrap.vue';
import configMixin from '../mixins/configMixin';
import availabilityMixin from '../mixins/availability';
import chatBotsMixin from '../mixins/chatBotsMixin';
import HiverBranding from 'widget/components/HiverBranding.vue';
import TeamAvailability from 'widget/components/TeamAvailability';
import Spinner from 'shared/components/Spinner.vue';
import Banner from 'widget/components/Banner.vue';
import { mapActions, mapGetters } from 'vuex';
import { MAXIMUM_FILE_UPLOAD_SIZE } from 'shared/constants/messages';
import { CAMPAIGN_TRIGGER } from 'shared/constants/conversation';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import PreChatForm from '../components/PreChat/Form';
import ContactForm from '../components/PreChat/ContactForm';
import { nextTick } from 'vue';
import dropdownImage from '../assets/images/dropdownIcon.svg';

export default {
  name: 'Home',
  components: {
    ChatFooter,
    ChatHeader,
    ChatHeaderExpanded,
    ConversationWrap,
    PreChatForm,
    ContactForm,
    Spinner,
    HiverBranding,
    TeamAvailability,
    Banner
  },
  mixins: [configMixin, availabilityMixin, chatBotsMixin],
  props: {
    hasFetched: {
      type: Boolean,
      default: false
    },
    showPopoutButton: {
      type: Boolean,
      default: false
    },
    chatTabProps: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isOnCollapsedView: false,
      isOnNewConversation: false,
      showContactFormViewBus: false,
      successScreenExpired: false,
      dropDownSymbolImageUrl: dropdownImage,
      dropDownSymbolImageAlt: 'DropDownIcon',
    };
  },
  computed: {
    ...mapGetters({
      availableAgents: 'agent/availableAgents',
      conversationAttributes: 'conversationAttributes/getConversationParams',
      conversationSize: 'conversation/getConversationSize',
      groupedMessages: 'conversation/getGroupedConversation',
      isFetchingList: 'conversation/getIsFetchingList',
      currentUser: 'contacts/getCurrentUser',
      hiverConfig: 'hiverConfig/getHiverConfig',
      isLoadingAvailableAgents: 'agent/getIsLoading',
      isCampaignMessageClicked: 'campaign/getIsCampaignMessageClicked',
      campaignTrigger: 'campaign/getCampaignTrigger',
    }),
    showContactFormView() {
      if ((this.conversationSize && !this.isOnNewConversation) ||  (!this.open && this.isChatAvailable)) {
        return false;
      }
      return (
        this.hiverConfig.show_contact_form || this.showContactFormViewBus
      );
    },
    currentView() {
      const { email: currentUserEmail = '' } = this.currentUser;
      if (this.showContactFormView && !this.successScreenExpired && !this.hasAConnectedAgentBot) {
        return 'contactFormView';
      }
      if (this.isCampaignMessageClicked && this.isChatAvailable && this.preChatFormEnabled && !currentUserEmail) {
        return 'preChatFormView';
      }
      if (this.isHeaderCollapsed) {
        if (this.conversationSize) {
          return 'messageView';
        }
        if (
          this.isOnNewConversation ||
          (this.preChatFormEnabled && !currentUserEmail)
        ) {
          return 'preChatFormView';
        }
        return 'messageView';
      }
      return 'cardView';
    },
    isOnline() {
      const { workingHoursEnabled } = this.channelConfig;
      const anyAgentOnline = this.availableAgents.length > 0;

      if (workingHoursEnabled ) {
        return (anyAgentOnline && this.isInBetweenTheWorkingHours) || this.hasAConnectedAgentBot;
      }
      return anyAgentOnline || this.hasAConnectedAgentBot;
    },
    isOpen() {
      return this.conversationAttributes.status === 'open';
    },
    fileUploadSizeLimit() {
      return MAXIMUM_FILE_UPLOAD_SIZE;
    },
    isHeaderCollapsed() {
      if (!this.hasIntroText || this.conversationSize) {
        return true;
      }

      return this.isOnCollapsedView;
    },
    hasIntroText() {
      return (
        this.channelConfig.welcomeTitle || this.channelConfig.welcomeTagline
      );
    },
    isSurfaceKbEnabled() {
      return this.isSurfaceKBInLiveChatEnabled;
    },
  },
  mounted() {
    bus.$on(BUS_EVENTS.SHOW_CONTACT_FORM, () => {
      this.isOnNewConversation = true;
      this.successScreenExpired = false;
      this.showContactFormViewBus = true;
    });
  },
  methods: {
    ...mapActions('conversation', [
      'clearConversations',
      'sendMessage',
      'registerChatsbotService',
    ]),
    ...mapActions('conversationAttributes', [
        'clearConversationAttributes'
    ]),
    onStartNewConversation() {
     if (this.hasAConnectedAgentBot) {
        let campaignData = {
            id: null,
            type: CAMPAIGN_TRIGGER.WIDGET
        };
        const isNudge = this.campaignTrigger?.type === CAMPAIGN_TRIGGER.NUDGE && this.campaignTrigger?.id;
        if (isNudge) {
            campaignData.id = this.campaignTrigger.id;
            campaignData.type = this.campaignTrigger.type;
        }
       this.handleChatbotFlowOnNewConversation(campaignData.id, campaignData.type, true);
       if (this.conversationSize && isNudge) {
            this.clearConversations();
            this.clearConversationAttributes();
        }
     } else {
      this.showContactFormViewBus = false;
      this.isOnCollapsedView = true;
      this.isOnNewConversation = true;
      this.handleChatbotFlowOnNewConversation();
     }
    },
    startConversation() {
      this.clearConversationAttributes();
      this.isOnCollapsedView = !this.isOnCollapsedView;
      this.adjustHeightForPreChatForm();
      if (!this.preChatFormEnabled) {
        return this.onStartNewConversation();
      }
      const { email: currentUserEmail = '' } = this.currentUser;
      if (this.hasAConnectedAgentBot) {
        const isNudge = this.campaignTrigger?.type === CAMPAIGN_TRIGGER.NUDGE && this.campaignTrigger?.id;

        if (this.preChatFormEnabled && currentUserEmail) {
            let campaignData = {
                id: null,
                type: CAMPAIGN_TRIGGER.WIDGET
            };
            if (isNudge) {
                campaignData.id = this.campaignTrigger.id;
                campaignData.type = this.campaignTrigger.type;
            }
            this.handleChatbotFlowOnNewConversation(campaignData.id, campaignData.type, true);
            return;
        }
      }
      else if(currentUserEmail){
        this.onStartNewConversation();
      }

    },
    onCfSuccessScreenExpired() {
        this.successScreenExpired = true;
        setTimeout(() => {
            this.successScreenExpired = false;
            this.isOnNewConversation = false;
        }, 100)
    },
    async adjustHeightForPreChatForm() {
      await nextTick();
      const headerWrap = document.getElementsByClassName('header-wrap');
      const inputContainer = document.getElementsByClassName(
        'inputs-container'
      );
      const height = headerWrap.length > 0 ? headerWrap[0].offsetHeight : 0;

      if (inputContainer && inputContainer.length > 0) {
        inputContainer[0].style.maxHeight = `calc(86vh - ${height}px)`;
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import '~widget/assets/scss/variables';
@import '~widget/assets/scss/mixins';

.home {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  overflow: hidden;
  background: $color-background;
  padding-bottom: 20px;
  font-family: 'Open Sans', sans-serif;
  .header-wrap {
    border-radius: $space-normal $space-normal 0 0;
    flex-shrink: 0;
    transition: max-height 300ms;
    z-index: 99;
    @include shadow-large;

    &.expanded {
      max-height: 16rem;
    }

    @media only screen and (min-device-width: 320px) and (max-device-width: 667px) {
      border-radius: 0;
    }
  }

  .footer-wrap {
    flex-shrink: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    position: relative;

    &:before {
      content: '';
      position: absolute;
      top: -$space-normal;
      left: 0;
      width: 100%;
      height: $space-normal;
      opacity: 0.1;
      background: linear-gradient(
        to top,
        $color-background,
        rgba($color-background, 0)
      );
    }
  }

  .input-wrap {
    padding: 0 $space-normal;
  }
}
</style>
